// IndexedDB Service for Offline Data Storage
const DB_NAME = 'benivet-offline-db';
const DB_VERSION = 1;

const STORES = {
  PATIENTS: 'patients',
  VISITS: 'visits',
  ANESTHESIA: 'anesthesia',
  OFFLINE_QUEUE: 'offline_queue',
  SETTINGS: 'settings',
  MEDICATION_SCHEDULES: 'medication_schedules'
};

let db = null;

// Initialize the database
export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Patients store
      if (!database.objectStoreNames.contains(STORES.PATIENTS)) {
        const patientsStore = database.createObjectStore(STORES.PATIENTS, { keyPath: 'id' });
        patientsStore.createIndex('name', 'name', { unique: false });
        patientsStore.createIndex('owner_name', 'owner_name', { unique: false });
      }

      // Visits store
      if (!database.objectStoreNames.contains(STORES.VISITS)) {
        const visitsStore = database.createObjectStore(STORES.VISITS, { keyPath: 'id' });
        visitsStore.createIndex('patient_id', 'patient_id', { unique: false });
        visitsStore.createIndex('date', 'date', { unique: false });
      }

      // Anesthesia records store
      if (!database.objectStoreNames.contains(STORES.ANESTHESIA)) {
        const anesthesiaStore = database.createObjectStore(STORES.ANESTHESIA, { keyPath: 'id' });
        anesthesiaStore.createIndex('patient_id', 'patient_id', { unique: false });
      }

      // Offline queue store
      if (!database.objectStoreNames.contains(STORES.OFFLINE_QUEUE)) {
        const queueStore = database.createObjectStore(STORES.OFFLINE_QUEUE, { keyPath: 'id', autoIncrement: true });
        queueStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Settings store
      if (!database.objectStoreNames.contains(STORES.SETTINGS)) {
        database.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
      }

      // Medication schedules store
      if (!database.objectStoreNames.contains(STORES.MEDICATION_SCHEDULES)) {
        const medStore = database.createObjectStore(STORES.MEDICATION_SCHEDULES, { keyPath: 'id' });
        medStore.createIndex('patient_id', 'patient_id', { unique: false });
        medStore.createIndex('next_dose', 'next_dose', { unique: false });
      }
    };
  });
};

// Generic CRUD operations
export const addItem = async (storeName, item) => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(item);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const getItem = async (storeName, id) => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const getAllItems = async (storeName) => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const deleteItem = async (storeName, id) => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const clearStore = async (storeName) => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// Bulk operations for syncing
export const bulkAddItems = async (storeName, items) => {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    items.forEach(item => store.put(item));
    
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

// Offline queue operations
export const addToOfflineQueue = async (request) => {
  return addItem(STORES.OFFLINE_QUEUE, {
    ...request,
    id: Date.now(),
    timestamp: new Date().toISOString()
  });
};

export const getOfflineQueue = async () => {
  return getAllItems(STORES.OFFLINE_QUEUE);
};

export const clearOfflineQueue = async () => {
  return clearStore(STORES.OFFLINE_QUEUE);
};

export const removeFromOfflineQueue = async (id) => {
  return deleteItem(STORES.OFFLINE_QUEUE, id);
};

// Settings operations
export const getSetting = async (key) => {
  const item = await getItem(STORES.SETTINGS, key);
  return item?.value;
};

export const setSetting = async (key, value) => {
  return addItem(STORES.SETTINGS, { key, value });
};

// Specific data operations
export const cachePatients = async (patients) => {
  return bulkAddItems(STORES.PATIENTS, patients);
};

export const getCachedPatients = async () => {
  return getAllItems(STORES.PATIENTS);
};

export const cacheVisits = async (visits) => {
  return bulkAddItems(STORES.VISITS, visits);
};

export const getCachedVisits = async () => {
  return getAllItems(STORES.VISITS);
};

export const cacheMedicationSchedules = async (schedules) => {
  return bulkAddItems(STORES.MEDICATION_SCHEDULES, schedules);
};

export const getCachedMedicationSchedules = async () => {
  return getAllItems(STORES.MEDICATION_SCHEDULES);
};

export { STORES };
